import { spawn } from 'node:child_process';
import { Command } from 'commander';
import { unlockVault } from './shared.js';
import type { VaultPlainItem } from '../../types/vault.types.js';
import type { VaultStoreService } from '../../services/vault-store.service.js';

interface SshOptions {
    reason?: string;
    passphrase?: string;
    recoveryKey?: string;
    dryRun?: boolean;
}

interface SshTarget {
    user: string;
    host: string;
    port: string;
    remoteCommand: string;
    jumpHost?: string;
}

interface ServerConnectionTarget {
    user: string;
    host: string;
    port: string;
    jumpHost?: string;
}

export type ReadOnlyValidationResult =
    | { allowed: true }
    | { allowed: false; reason: string };

const SIMPLE_READ_ONLY_COMMANDS = new Set([
    'ss',
    'netstat',
    'lsof',
    'ps',
    'top',
    'uptime',
    'free',
    'df',
    'du',
    'ls',
    'cat',
    'tail',
    'head',
    'grep',
    'awk',
    'sed',
    'find',
    'stat',
    'journalctl',
]);

const BLOCKED_COMMANDS = new Set([
    'rm',
    'mv',
    'cp',
    'chmod',
    'chown',
    'mkdir',
    'touch',
    'tee',
    'kill',
    'pkill',
    'reboot',
    'shutdown',
    'apt',
    'apt-get',
    'yum',
    'dnf',
    'apk',
    'pacman',
    'brew',
    'npm',
    'pnpm',
    'yarn',
]);

const SYSTEMCTL_READ_ONLY = new Set(['status', 'show', 'list-units', 'list-unit-files', 'is-active', 'is-enabled']);
const SYSTEMCTL_BLOCKED = new Set(['restart', 'start', 'stop', 'reload', 'enable', 'disable', 'reset-failed']);
const SERVICE_BLOCKED = new Set(['restart', 'start', 'stop', 'reload']);
const PM2_READ_ONLY = new Set(['status', 'list', 'logs', 'describe']);
const PM2_BLOCKED = new Set(['restart', 'start', 'stop', 'delete', 'reload']);
const DOCKER_READ_ONLY = new Set(['ps', 'logs', 'inspect']);
const DOCKER_BLOCKED = new Set(['restart', 'start', 'stop', 'rm', 'compose']);
const GIT_READ_ONLY = new Set(['status', 'log', 'show', 'diff']);

export function joinRemoteCommand(command: string[]): string {
    return command.join(' ').trim();
}

export function buildSudoLoginCommand(runAsUser: string, command: string[]): string {
    if (!/^[a-z_][a-z0-9_-]*[$]?$/i.test(runAsUser)) {
        throw new Error(`Invalid Linux user "${runAsUser}".`);
    }
    const remoteCommand = joinRemoteCommand(command);
    if (!remoteCommand) {
        throw new Error('Command required after linux user.');
    }
    return `sudo -iu ${runAsUser} -- ${remoteCommand}`;
}

export function buildSshArgs(target: SshTarget): string[] {
    const args: string[] = [];
    if (target.jumpHost) args.push('-J', target.jumpHost);
    args.push('-p', target.port, `${target.user}@${target.host}`, target.remoteCommand);
    return args;
}

function quoteArgForDisplay(arg: string): string {
    return arg.includes(' ') ? JSON.stringify(arg) : arg;
}

function getStructuredServerField(item: VaultPlainItem, ...keys: string[]): string {
    if (typeof item.body === 'string') return '';
    for (const key of keys) {
        const value = item.body[key];
        if (typeof value === 'string' || typeof value === 'number') return String(value);
    }
    return '';
}

function assertStructuredServer(item: VaultPlainItem, commandName: string): void {
    if (item.type !== 'server' || typeof item.body === 'string') {
        throw new Error(`${commandName} requires a structured server item.`);
    }
}

function buildJumpHost(target: ServerConnectionTarget): string {
    const base = `${target.user}@${target.host}`;
    return target.port && target.port !== '22' ? `${base}:${target.port}` : base;
}

async function resolveServerConnectionTarget(
    store: VaultStoreService,
    vmk: Buffer,
    id: string,
    commandName: string
): Promise<ServerConnectionTarget> {
    const item = await store.readPlainItem(vmk, id);
    assertStructuredServer(item, commandName);

    const host = getStructuredServerField(item, 'host', 'hostname');
    const user = getStructuredServerField(item, 'user', 'username');
    const port = getStructuredServerField(item, 'port') || '22';
    if (!host || !user) throw new Error('Server item requires host and user fields.');

    const target: ServerConnectionTarget = { user, host, port };
    const bastionId = getStructuredServerField(item, 'bastion');
    if (!bastionId) return target;

    const bastion = await store.readPlainItem(vmk, bastionId);
    assertStructuredServer(bastion, 'Vault bastion resolver');
    const bastionHost = getStructuredServerField(bastion, 'host', 'hostname');
    const bastionUser = getStructuredServerField(bastion, 'user', 'username');
    const bastionPort = getStructuredServerField(bastion, 'port') || '22';
    if (!bastionHost || !bastionUser) {
        throw new Error(`Bastion item "${bastionId}" requires host and user fields.`);
    }

    target.jumpHost = buildJumpHost({ user: bastionUser, host: bastionHost, port: bastionPort });
    return target;
}

function tokenizeCommandSegment(segment: string): string[] {
    const matches = segment.match(/"([^"\\]|\\.)*"|'([^'\\]|\\.)*'|\S+/g) || [];
    return matches.map((token) => token.replace(/^(['"])(.*)\1$/, '$2'));
}

function commandBasename(command: string): string {
    return command.split('/').pop() || command;
}

function commandAfterSudo(tokens: string[]): string[] {
    if (commandBasename(tokens[0] || '') !== 'sudo') return tokens;

    let index = 1;
    while (index < tokens.length) {
        const token = tokens[index];
        if (token === '--') {
            index += 1;
            break;
        }
        if (!token.startsWith('-')) break;

        if (token === '-u' || token === '--user' || token === '-g' || token === '--group') {
            index += 2;
            continue;
        }
        if (/^--(?:user|group)=/.test(token)) {
            index += 1;
            continue;
        }
        if (/^-[^-]*[ug][^-]*$/.test(token)) {
            index += 2;
            continue;
        }
        index += 1;
    }

    return tokens.slice(index);
}

function validateReadOnlySegment(segment: string): ReadOnlyValidationResult {
    const tokens = commandAfterSudo(tokenizeCommandSegment(segment.trim()));
    if (tokens.length === 0) return { allowed: false, reason: 'Command segment is empty.' };

    const command = commandBasename(tokens[0]);
    const subcommand = tokens[1] || '';

    if (BLOCKED_COMMANDS.has(command)) {
        return { allowed: false, reason: `Command "${command}" can change server state.` };
    }

    if (command === 'systemctl') {
        if (SYSTEMCTL_BLOCKED.has(subcommand)) {
            return { allowed: false, reason: `systemctl ${subcommand} can change service state.` };
        }
        return SYSTEMCTL_READ_ONLY.has(subcommand)
            ? { allowed: true }
            : { allowed: false, reason: `systemctl ${subcommand || '(missing action)'} is not in the read-only allowlist.` };
    }

    if (command === 'service') {
        if (SERVICE_BLOCKED.has(subcommand)) {
            return { allowed: false, reason: `service ${subcommand} can change service state.` };
        }
        return subcommand === 'status'
            ? { allowed: true }
            : { allowed: false, reason: `service ${subcommand || '(missing action)'} is not in the read-only allowlist.` };
    }

    if (command === 'pm2') {
        if (PM2_BLOCKED.has(subcommand)) {
            return { allowed: false, reason: `pm2 ${subcommand} can change process state.` };
        }
        return PM2_READ_ONLY.has(subcommand)
            ? { allowed: true }
            : { allowed: false, reason: `pm2 ${subcommand || '(missing action)'} is not in the read-only allowlist.` };
    }

    if (command === 'docker') {
        if (DOCKER_BLOCKED.has(subcommand)) {
            return { allowed: false, reason: `docker ${subcommand} can change container state.` };
        }
        return DOCKER_READ_ONLY.has(subcommand)
            ? { allowed: true }
            : { allowed: false, reason: `docker ${subcommand || '(missing action)'} is not in the read-only allowlist.` };
    }

    if (command === 'git') {
        return GIT_READ_ONLY.has(subcommand)
            ? { allowed: true }
            : { allowed: false, reason: `git ${subcommand || '(missing action)'} is not in the read-only allowlist.` };
    }

    return SIMPLE_READ_ONLY_COMMANDS.has(command)
        ? { allowed: true }
        : { allowed: false, reason: `Command "${command}" is not in the read-only allowlist.` };
}

export function validateReadOnlyRemoteCommand(command: string): ReadOnlyValidationResult {
    const trimmed = command.trim();
    if (!trimmed) return { allowed: false, reason: 'ssh-ro requires a command.' };
    if (/(^|[^<])>{1,2}/.test(trimmed)) {
        return { allowed: false, reason: 'Output redirection can write files on the server.' };
    }
    if (/[;&`]/.test(trimmed) || /\$\(/.test(trimmed) || /\|\|/.test(trimmed) || /&&/.test(trimmed)) {
        return { allowed: false, reason: 'Shell chaining or command substitution is not allowed in read-only mode.' };
    }
    if (/\b(?:bash|sh|zsh|fish)\s+-c\b/.test(trimmed)) {
        return { allowed: false, reason: 'Shell wrappers such as bash -c are not allowed in read-only mode.' };
    }

    for (const segment of trimmed.split('|')) {
        const result = validateReadOnlySegment(segment);
        if (!result.allowed) return result;
    }
    return { allowed: true };
}

async function runVaultSsh(
    id: string,
    remoteCommand: string,
    options: SshOptions,
    mode: { readOnly?: boolean } = {}
): Promise<void> {
    const { store, vmk } = await unlockVault(options);
    const target = await resolveServerConnectionTarget(store, vmk, id, 'j vault ssh');
    if (!remoteCommand) {
        console.log(mode.readOnly ? `Usage: j vault ssh-ro ${id} pm2 status` : `Usage: j vault ssh ${id} pm2 status`);
        return;
    }
    if (mode.readOnly) {
        const result = validateReadOnlyRemoteCommand(remoteCommand);
        if (!result.allowed) {
            throw new Error([
                `ssh-ro blocked command: ${result.reason}`,
                `Nếu thật sự cần execution mode, dùng: j v ssh ${id} --reason "<why>" ${JSON.stringify(remoteCommand)}`,
            ].join('\n'));
        }
    }
    const args = buildSshArgs({ ...target, remoteCommand });
    if (options.dryRun) {
        console.log(`ssh ${args.map(quoteArgForDisplay).join(' ')}`);
        return;
    }
    await new Promise<void>((resolve, reject) => {
        const child = spawn('ssh', args, { stdio: 'inherit' });
        child.on('error', reject);
        child.on('close', (code) => {
            if (code === 0) resolve();
            else reject(new Error(`ssh exited with code ${code}`));
        });
    });
}

export function createVaultSshCommand(): Command {
    return new Command('ssh')
        .description('Run a safe SSH command using a server item from vault')
        .option('--reason <reason>', 'Operational reason for audit')
        .option('--passphrase <value>', 'Vault passphrase')
        .option('--recovery-key <value>', 'Recovery key')
        .option('--dry-run', 'Print the SSH command without executing')
        .argument('<id>', 'Vault server item id')
        .argument('[command...]', 'Remote command to run')
        .action(async (id: string, command: string[], options: SshOptions) => {
            await runVaultSsh(id, joinRemoteCommand(command), options);
        });
}

export function createVaultSshAsCommand(): Command {
    return new Command('ssh-as')
        .description('Run a safe SSH command as a Linux user via sudo login shell')
        .option('--reason <reason>', 'Operational reason for audit')
        .option('--passphrase <value>', 'Vault passphrase')
        .option('--recovery-key <value>', 'Recovery key')
        .option('--dry-run', 'Print the SSH command without executing')
        .argument('<id>', 'Vault server item id')
        .argument('<user>', 'Linux user to sudo into')
        .argument('[command...]', 'Remote command to run')
        .action(async (id: string, runAsUser: string, command: string[], options: SshOptions) => {
            await runVaultSsh(id, buildSudoLoginCommand(runAsUser, command), options);
        });
}

export function createVaultSshRoCommand(): Command {
    return new Command('ssh-ro')
        .description('Run a read-only SSH investigation command using a server item from vault')
        .option('--passphrase <value>', 'Vault passphrase')
        .option('--recovery-key <value>', 'Recovery key')
        .option('--dry-run', 'Print the SSH command without executing')
        .argument('<id>', 'Vault server item id')
        .argument('[command...]', 'Read-only remote command to run')
        .action(async (id: string, command: string[], options: SshOptions) => {
            const remoteCommand = joinRemoteCommand(command);
            if (!remoteCommand) throw new Error('j vault ssh-ro requires a read-only command.');
            await runVaultSsh(id, remoteCommand, options, { readOnly: true });
        });
}
